import React from 'react';
import { action, observable, computed } from "mobx";
import { observer } from "mobx-react";
import { withRouter } from 'react-router-dom';
import { withStyles } from "@material-ui/core/styles";
import { grey } from "@material-ui/core/colors";
import Grid from '@material-ui/core/Grid';
import Skeleton from '@material-ui/lab/Skeleton';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import LinearProgress from '@material-ui/core/LinearProgress';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import NotInterestedIcon from '@material-ui/icons/NotInterested';
import CompareIcon from '@material-ui/icons/Compare';

import { athenaBlue, athenaGreen, statRed } from '../globals';
import Header from '../components/utils/Header';
import Substep from '../api/Substep';
import LoadingCheckbox from '../components/form/LoadingCheckbox';
import { cycle } from '../store/Cycle';
import { chainAsyncCalls } from '../utils';
import {
  DemandIcon, FileIcon, MachineIcon, PersonIcon, RawIcon, ScenarioIcon, SupplyIcon
} from "../theme/AthenaIcons";
import PermissionChecker from '../components/permissions/PermissionChecker';


const styles = theme => ({
  header_numberStep_number: {
    borderRadius: "20px",
    padding: '4px',
    backgroundColor: grey[200],
  },
  bolder: {
    fontWeight: '800',
  },
  detail_numberStep_number: {
    borderRadius: "20px",
    padding: '4px',
    backgroundColor: grey[200],
  },
  green: {
    color: athenaGreen,
  },
  red: {
    color: statRed,
  },
  blackFont: {
    color: '#333',
  },
  greyTitle: {
    color: '#727272',
    fontWeight: 900,
  },
  lightGreyTitle: {
    color: '#b1b1b1',
    fontWeight: 900,
  },
  progressContainer: {
    flexBasis: 250,
  },
  blueTitle: {
    color: theme.palette.primary.main,
    fontWeight: 900,
  },
  paddingLeftAccordion: {
    paddingLeft: '40px !important',
  },
  paddingRightAccordion: {
    paddingRight: '40px !important',
  },
  postNumberPadding: {
    paddingLeft: 35,
  },
});

const LinearProgressBlue = withStyles({
  colorPrimary: {
    backgroundColor: 'rgba(205,205,205, 0.5)',
  },
  barColorPrimary: {
    backgroundColor: athenaBlue,
  },
})(LinearProgress);
const LinearProgressGreen = withStyles({
  colorPrimary: {
    backgroundColor: 'rgba(205,205,205, 0.5)',
  },
  barColorPrimary: {
    backgroundColor: athenaGreen,
  },
})(LinearProgress);

@withStyles(styles)
@withRouter
@observer
class CycleWorkflowView extends React.Component {
  @observable loading = true;
  @observable updating = true;
  @observable substeps = null;

  componentDidMount() {
    this.updateData();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if ((this.props.location !== prevProps.location) && !this.updating) {
      this.updateData();
    }
  }

  @action updateData = chainAsyncCalls(async () => {
    this.updating = true;
    this.substeps = await Substep.getList(cycle.params);
    this.loading = false;
    this.updating = false;
  });

  get cycleData() {
    return {
      month: cycle.longMonth,
      year: cycle.year,
      status: 'On-going', // Or Past & Validated
      start_date: 'November 26th 2019',
      end_date: 'December 26th 2019',
    };
  }

  get stepsData(){
    const steps = {};
    for(const substep of this.substeps){
      const key = substep.step_position;
      if (!(key in steps)){
        steps[key] = { step: substep.step, substeps: [] };
      }
      steps[key].substeps.push(substep);
    }
    return steps;
  }

  @computed get completed() {
    let result = {};
    Object.entries(this.stepsData).forEach(([stepName, stepInfo]) => {
      const size = stepInfo.substeps.length;
      const checked = stepInfo.substeps.filter(substep => substep.checked).length;
      result[stepName] = (checked*100)/size;
    });
    return result;
  }

  @computed get globalProgress() {
    const values = Object.values(this.completed);
    if (values.length === 0) {
      return 0;
    }
    return values.reduce((a,b) => a + b) / values.length
  }

  @action onCheckChange = async (substep) => {
    substep.isFetched = false;
    await substep.ajaxRequest('put', `/toggle_substep/`);
    await this.updateData();
    substep.isFetched = true;
  };

  get steps() {
    if (this.loading) {
      return <Skeleton variant="rect" width="100%" height={550} />;
    }
    const { classes } = this.props;
    const numberSteps = (substeps) => {
      const size = substeps.length;
      const checked = substeps.filter(substep => substep.checked).length;
      return `${checked}/${size}`
    };
    const placeStatus = (check) => check ? <CheckCircleIcon className={classes.green}/> : <CancelIcon className={classes.red}/>;
    const placeAction = (action) => action==='automatic' ? <MachineIcon />: <PersonIcon />;
    const placeLinkSubStep = (url) => {
      const styles = { stroke: '#00a8d7', strokeWidth: 2, fontSize: 'large' };
      if (url.indexOf('input-files') >= 0) {
        return <FileIcon {...styles} />
      } else if (url.indexOf('scenarios') >= 0) {
        return <ScenarioIcon {...styles} />
      } else if (url.indexOf('demand') >= 0) {
        return <DemandIcon {...styles} />
      } else if (url.indexOf('compare') >= 0) {
        return <CompareIcon color="primary"/>
      } else if (url.indexOf('supply') >= 0) {
        return <SupplyIcon {...styles} />
      } else if (url.indexOf('raw-and-packs') >= 0) {
        return <RawIcon {...styles} />
      } else { // Empty
        return <NotInterestedIcon color="primary"/>
      }
    };
    const getClass = progress =>  progress>=100 || progress===0 ? 'greyTitle' : 'blueTitle';
    return (
      <Grid>
        {Object.entries(this.stepsData).map(([stepPosition, {substeps, step}])=>(
          <Accordion key={stepPosition}>
            <AccordionSummary wrap="nowrap">
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={1} className={classes.paddingLeftAccordion}>
                  <Typography noWrap className={classes[getClass(this.completed[stepPosition])]}>
                    <span>#{stepPosition}</span>
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography noWrap className={classes[getClass(this.completed[stepPosition])]}>
                    {step}
                  </Typography>
                </Grid>
                <Grid item xs={2} className={classes.paddingRightAccordion}>
                  <Grid container justify="flex-end" spacing={2}>
                    <Typography noWrap className={classes[getClass(this.completed[stepPosition])]}>
                      <span>{numberSteps(substeps)}</span>
                    </Typography>
                    <span className={classes[getClass(this.completed[stepPosition])]}><ExpandMoreIcon/></span>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  {
                    this.completed[stepPosition]===100
                    ? <LinearProgressGreen variant="determinate" value={this.completed[stepPosition]}/>
                    : <LinearProgressBlue variant="determinate" value={this.completed[stepPosition]}/>
                  }
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container alignItems="center" justify="flex-end">
                <Grid item xs={5} className={classes.paddingLeftAccordion}>
                  <Typography noWrap className={classes.lightGreyTitle}>
                    Task Name
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <Typography noWrap className={classes.lightGreyTitle}>
                    In Charge
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <Typography noWrap className={classes.lightGreyTitle}>
                    Target date
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <Typography noWrap className={classes.lightGreyTitle}>
                    Status
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <Typography noWrap className={classes.lightGreyTitle}>
                    Check
                  </Typography>
                </Grid>
                <Grid item xs={1}>
                  <Typography noWrap className={classes.lightGreyTitle}>
                    Date
                  </Typography>
                </Grid>
                <Grid item xs={1} className={classes.paddingRightAccordion}>
                  <Typography noWrap className={classes.lightGreyTitle}>
                    Action
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
            {substeps.map(substep => (
              <AccordionDetails
                key={substep.id}
              >
                <Grid container alignItems="center">
                  <Grid item xs={6} className={classes.paddingLeftAccordion}>
                    <PermissionChecker permission="athena.change_substep">
                      {
                        hasPermission =>
                          <Typography>
                            <LoadingCheckbox
                              substep={substep}
                              loading={!substep.isFetched}
                              onCheckChange={this.onCheckChange}
                              numberSteps={numberSteps(substeps)}
                              disabled={!hasPermission}
                            />
                            <strong><span className={classes[`${substep.checked ? 'green' : 'red'}`]}>#{substep.substep_position}</span></strong>
                            <span className={classes.postNumberPadding}>{substep.name}</span>
                          </Typography>
                      }
                    </PermissionChecker>
                  </Grid>
                  <Grid item xs={1}>
                    <Typography noWrap className={classes[getClass(this.completed[stepPosition])]}>
                      {substep.responsible}
                    </Typography>
                  </Grid>
                  <Grid item xs={1}>
                    <Typography noWrap>
                      {substep.target_date? new Date(substep.target_date).toLocaleDateString('en-US') : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={1}>
                    <Typography noWrap>
                      {placeStatus(substep.checked)}
                    </Typography>
                  </Grid>
                  <Grid item xs={1}>
                    <Typography noWrap>
                      {placeAction(substep.action)}
                    </Typography>
                  </Grid>
                  <Grid item xs={1}>
                    <Typography noWrap>
                      {substep.checked_at? new Date(substep.checked_at).toLocaleDateString('en-US') : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={1} className={classes.paddingRightAccordion}>
                    <Typography noWrap>
                      <Link href={substep.url_action || '#'}>
                        {placeLinkSubStep(substep.url_action)}
                      </Link>
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            ))}
          </Accordion>
        ))}
      </Grid>
    );
  }

  get cycle() {
    const cycle = this.cycleData;
    return (
      <Header title={`CYCLE: ${cycle.month.toUpperCase()} ${cycle.year}`} />
    )
  }

  get globalProgressView() {
    const { classes } = this.props;
    if (this.loading) {
      return <Skeleton variant="rect" width="100%" height={55} />;
    }
    return (
      <>
        <Grid container justify="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h6" noWrap align='right'>
              Cycle completion
            </Typography>
          </Grid>
          <Grid item>
            <span className={classes.blueTitle}>{this.globalProgress.toFixed(0)}%</span>
          </Grid>
        </Grid>
        <LinearProgressGreen variant="determinate" value={this.globalProgress}/>
      </>
    );
  }

  render() {
    const { classes } = this.props;
    return (
      <Grid container direction="column" spacing={2}>
        <Grid item container spacing={2} justify="space-between" alignItems="center">
          <Grid item>
            {this.cycle}
          </Grid>
          <Grid item className={classes.progressContainer}>
            {this.globalProgressView}
          </Grid>
        </Grid>
        <Grid item>
          {this.steps}
        </Grid>
      </Grid>
    );
  }
}


export default CycleWorkflowView;
